<template>
  <div class="store-management">
    <!-- 页面头部 -->
    <div class="page-header">
      <div>
        <h2 class="page-title">门店管理</h2>
        <p class="page-desc">管理您的门店信息，支持多门店运营</p>
      </div>
      <el-button type="primary" size="large" @click="openAddDialog">
        <el-icon>
          <Plus />
        </el-icon>
        新增门店
      </el-button>
    </div>

    <!-- 搜索筛选栏 -->
    <div class="filter-bar">
      <div class="filter-left">
        <el-input v-model="searchKey" placeholder="搜索门店名称或地址" prefix-icon="Search" clearable style="width: 280px"
          @keyup.enter="handleSearch" />
        <el-select v-model="statusFilter" placeholder="全部状态" clearable style="width: 120px">
          <el-option label="营业中" :value="1" />
          <el-option label="已关闭" :value="0" />
        </el-select>
        <el-button type="primary" @click="handleSearch">查询</el-button>
        <el-button @click="resetSearch">重置</el-button>
      </div>
    </div>

    <!-- 门店统计卡片 - 使用 statistics 数据 -->
    <div class="stat-cards">
      <div class="stat-card">
        <div class="stat-icon" style="background: #e8f3ff;">
          <el-icon color="#409eff" :size="28">
            <Shop />
          </el-icon>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ statistics.total_count }}</div>
          <div class="stat-label">总门店数</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon" style="background: #e6f7e6;">
          <el-icon color="#67c23a" :size="28">
            <CircleCheck />
          </el-icon>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ statistics.active_count }}</div>
          <div class="stat-label">营业中</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon" style="background: #fff0e6;">
          <el-icon color="#e6a23c" :size="28">
            <Star />
          </el-icon>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ statistics.avg_rating }}</div>
          <div class="stat-label">平均评分</div>
        </div>
      </div>
    </div>

    <!-- 门店列表 -->
    <div class="store-grid">
      <div v-for="store in storeList" :key="store.id" class="store-card" @click="viewStoreDetail(store)">
        <div class="store-cover">
          <img :src="store.images?.[0] || defaultCover" :alt="store.store_name" />
          <div class="store-status" :class="store.status === 1 ? 'status-active' : 'status-closed'">
            {{ store.status === 1 ? '营业中' : '已关闭' }}
          </div>
        </div>
        <div class="store-info">
          <h3 class="store-name">{{ store.store_name }}</h3>
          <div class="store-address">
            <el-icon>
              <Location />
            </el-icon>
            <span>{{ store.address }}</span>
          </div>
          <div class="store-meta">
            <div class="store-rating">
              <el-rate v-model="store.rating" disabled :text-color="'#ff9a2e'" />
              <span class="rating-score">{{ store.rating }}</span>
            </div>
            <div class="store-price">人均 ¥{{ store.avg_price }}</div>
          </div>
          <div class="store-features">
            <el-tag v-for="(feature, idx) in store.features?.slice(0, 3)" :key="idx" size="small" round>
              {{ feature }}
            </el-tag>
            <el-tag v-if="store.features?.length > 3" size="small" round type="info">
              +{{ store.features.length - 3 }}
            </el-tag>
          </div>
        </div>
        <div class="store-actions" @click.stop>
          <el-button type="primary" link @click="openEditDialog(store)">
            <el-icon>
              <Edit />
            </el-icon> 编辑
          </el-button>
          <el-button type="info" link class="dashboard-btn" size="small"
            @click="goToDashboard(store.id, store.store_name)">
            <el-icon>
              <DataAnalysis />
            </el-icon> 数据大屏
          </el-button>
          <el-popconfirm title="确定要删除该门店吗？" @confirm="handleDelete(store.id)" :teleported="false">
            <template #reference>
              <el-button type="danger" link>
                <el-icon>
                  <Delete />
                </el-icon> 删除
              </el-button>
            </template>
          </el-popconfirm>
        </div>
      </div>

      <div v-if="storeList.length === 0 && !loading" class="empty-state">
        <el-empty description="暂无门店数据，点击右上角新增门店" />
      </div>
    </div>

    <!-- 分页 -->
    <div class="pagination-wrapper" v-if="pagination.total > pagination.limit">
      <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.limit"
        :total="pagination.total" :page-sizes="[6, 12, 18, 24]" layout="total, sizes, prev, pager, next"
        @size-change="fetchData" @current-change="fetchData" />
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog v-model="dialogVisible" :title="isEditMode ? '编辑门店' : '新增门店'" width="680px" class="store-dialog"
      destroy-on-close>
      <el-form :model="formData" :rules="formRules" ref="formRef" label-width="100px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="门店名称" prop="store_name" required>
              <el-input v-model="formData.store_name" placeholder="请输入门店名称" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="门店编号" prop="store_no">
              <el-input v-model="formData.store_no" placeholder="自动生成" disabled />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="地图定位">
          <div style="display: flex; gap: 12px; flex-wrap: wrap; align-items: center; margin-bottom: 8px;">
            <el-button type="primary" plain @click="openMapPicker">
              <el-icon>
                <MapLocation />
              </el-icon> 在地图上选择位置
            </el-button>
            <el-button type="success" plain @click="openParseDialog">
              <el-icon>
                <Link />
              </el-icon> 一键解析
            </el-button>
          </div>
          <el-row :gutter="12">
            <el-col :span="12">
              <el-input v-model="lngInput" placeholder="经度（如：108.200180）" @change="handleLngChange">
                <template #prepend>经度</template>
              </el-input>
            </el-col>
            <el-col :span="12">
              <el-input v-model="latInput" placeholder="纬度（如：34.260360）" @change="handleLatChange">
                <template #prepend>纬度</template>
              </el-input>
            </el-col>
          </el-row>
          <div class="location-tip" v-if="formData.lng && formData.lat">
            <el-icon>
              <Location />
            </el-icon>
            <span>已定位：{{ formatCoordinate(formData.lng) }}, {{ formatCoordinate(formData.lat) }}</span>
          </div>
        </el-form-item>

        <el-form-item label="门店地址" prop="address" required>
          <el-input v-model="formData.address" placeholder="请输入详细地址" />
        </el-form-item>

        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="省份">
              <el-input v-model="formData.province" placeholder="省" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="城市">
              <el-input v-model="formData.city" placeholder="市" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="区域">
              <el-input v-model="formData.district" placeholder="区" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="联系电话">
          <el-input v-model="formData.contact_phone" placeholder="联系电话" />
        </el-form-item>

        <el-form-item label="营业时间">
          <el-time-picker v-model="businessHours" is-range range-separator="至" start-placeholder="开始时间"
            end-placeholder="结束时间" value-format="HH:mm:ss" format="HH:mm:ss" style="width: 100%" />
        </el-form-item>

        <el-form-item label="人均消费">
          <el-input-number v-model="formData.avg_price" :min="0" :precision="2" :step="10" />
        </el-form-item>

        <el-form-item label="评分">
          <div style="display: flex; align-items: center; gap: 10px;">
            <el-rate v-model="formData.rating" :max="5" show-score allow-half />
            <span style="color: #909399;">评价数：</span>
            <el-input-number v-model="formData.rating_count" :min="0" :step="1" controls-position="right"
              style="width: 120px" />
          </div>
        </el-form-item>

        <el-form-item label="门店特色">
          <el-select v-model="formData.features" multiple filterable allow-create default-first-option
            placeholder="请选择或输入门店特色" style="width: 100%">
            <el-option v-for="item in featureOptions" :key="item" :label="item" :value="item" />
          </el-select>
        </el-form-item>

        <el-form-item label="门店图片">
          <div class="image-uploader">
            <el-upload :file-list="imageFiles" action="#" :limit="5" :auto-upload="false" :on-change="handleImageChange"
              :on-remove="handleImageRemove" list-type="picture-card" accept="image/*">
              <el-icon>
                <Plus />
              </el-icon>
              <template #tip>
                <div class="upload-tip">建议尺寸 800x600px，最多上传5张</div>
              </template>
            </el-upload>
          </div>
        </el-form-item>

        <el-form-item label="营业状态">
          <el-switch v-model="formData.status" :active-value="1" :inactive-value="0" active-text="营业中"
            inactive-text="已关闭" />
        </el-form-item>

        <el-form-item label="排序">
          <el-input-number v-model="formData.sort_order" :min="0" :max="999" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">确 定</el-button>
      </template>
    </el-dialog>

    <!-- 地图选点弹窗 -->
    <el-dialog v-model="mapDialogVisible" title="选择门店位置" width="800px" destroy-on-close>
      <MapPicker v-if="mapDialogVisible" v-model="tempLocation" :mapKey="mapKey" @confirm="handleMapConfirm"
        @cancel="mapDialogVisible = false" />
    </el-dialog>

    <!-- 一键解析弹窗 -->
    <el-dialog v-model="parseDialogVisible" title="一键解析地图链接" width="600px" class="parse-dialog" destroy-on-close>
      <div class="parse-content">
        <el-alert title="提示：分享地图的链接，定位更准确哟！" type="info" :closable="false" show-icon style="margin-bottom: 16px;" />

        <el-form>
          <el-form-item label="地图链接">
            <el-input v-model="parseUrl" type="textarea" :rows="3" placeholder="请粘贴腾讯、百度或高德地图的分享链接" />
          </el-form-item>
        </el-form>

        <div class="parse-tips">
          <div class="tips-title">支持：</div>
          <div class="tips-buttons">
            <span class="tip-link tencent" @click="showTutorial('tencent')">腾讯地图</span>
            <span class="tip-link baidu" @click="showTutorial('baidu')">百度地图</span>
            <span class="tip-link amap" @click="showTutorial('amap')">高德地图</span>
          </div>
          <div class="tips-desc">点击上方链接可查看对应地图的分享教程</div>
        </div>
      </div>

      <template #footer>
        <el-button @click="parseDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleParseUrl" :loading="parseLoading">解析并填充</el-button>
      </template>
    </el-dialog>

    <!-- 教程图片弹窗 -->
    <el-dialog v-model="tutorialDialogVisible" :title="tutorialTitle" width="80%" destroy-on-close>
      <div class="tutorial-image-container">
        <img :src="tutorialImageUrl" alt="教程图片" style="width: 100%;" />
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { ElMessage, ElLoading } from 'element-plus'
import Cookies from 'js-cookie'
import axios from 'axios'
import config from '@/config'
import { getUploadSignature, uploadToOSS } from '@/utils/uploadUtils'
import { Plus, Edit, Delete, Shop, Location, Star, CircleCheck, MapLocation, DataAnalysis, Link } from '@element-plus/icons-vue'
import { useRouter } from 'vue-router'
import MapPicker from '@/components/MapPicker.vue'

const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
const agentId = userInfo.agent_id || 0
const storeUserId = userInfo.id

const loading = ref(false)
const submitting = ref(false)
const searchKey = ref('')
const statusFilter = ref(null)
const storeList = ref([])
const dialogVisible = ref(false)
const isEditMode = ref(false)
const formRef = ref(null)
const imageFiles = ref([])
const router = useRouter()
const defaultCover = 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png'

// 新增：统计数据
const statistics = ref({
  total_count: 0,
  active_count: 0,
  avg_rating: '0.0'
})

// 经纬度输入框的绑定值（字符串格式，方便编辑）
const lngInput = ref('')
const latInput = ref('')


// 在变量声明部分添加
const parseDialogVisible = ref(false)
const parseUrl = ref('')
const parseLoading = ref(false)
const tutorialDialogVisible = ref(false)
const tutorialTitle = ref('')
const tutorialImageUrl = ref('')

// 导入教程图片（假设图片放在 assets/images/ 目录下）
import tencentTutorial from '@/assets/images/tencent_tutorial.jpg'
import baiduTutorial from '@/assets/images/baidu_tutorial.jpg'
import amapTutorial from '@/assets/images/amap_tutorial.jpg'

const featureOptions = [
  '可堂食', '可停车', '有包间', '有WiFi', '充电宝',
  '可预约', '可吸烟', '儿童区', '露天座位', '宠物友好'
]

const formData = reactive({
  id: null,
  agent_id: agentId,
  store_id: storeUserId,
  store_no: '',
  store_name: '',
  address: '',
  province: '',
  city: '',
  district: '',
  lng: null,
  lat: null,
  contact_phone: '',
  business_hours: '',
  avg_price: 0,
  rating: 0,
  rating_count: 0,
  features: [],
  images: [],
  status: 1,
  sort_order: 0
})

// 监听 formData 变化，同步经纬度输入框
watch(() => formData.lng, (newVal) => {
  lngInput.value = newVal !== null && newVal !== undefined ? String(newVal) : ''
})

watch(() => formData.lat, (newVal) => {
  latInput.value = newVal !== null && newVal !== undefined ? String(newVal) : ''
})


// 打开一键解析弹窗
const openParseDialog = () => {
  parseUrl.value = ''
  parseDialogVisible.value = true
}

// 显示教程图片
const showTutorial = (type) => {
  if (type === 'tencent') {
    tutorialTitle.value = '腾讯地图分享教程'
    tutorialImageUrl.value = tencentTutorial
  } else if (type === 'baidu') {
    tutorialTitle.value = '百度地图分享教程'
    tutorialImageUrl.value = baiduTutorial
  } else if (type === 'amap') {
    tutorialTitle.value = '高德地图分享教程'
    tutorialImageUrl.value = amapTutorial
  }
  tutorialDialogVisible.value = true
}

// 解析地图链接
const handleParseUrl = async () => {
  if (!parseUrl.value.trim()) {
    ElMessage.warning('请输入地图链接')
    return
  }

  parseLoading.value = true
  try {
    const response = await axios.post(`${config.BASE_URL}/shop/api/parse_map_url.php`, {
      url: parseUrl.value.trim()
    })

    if (response.data.code === 200) {
      const { lng, lat } = response.data.data
      formData.lng = lng
      formData.lat = lat
      lngInput.value = String(lng)
      latInput.value = String(lat)
      ElMessage.success(`解析成功！${response.data.data.map_type} 坐标已自动填充`)
      parseDialogVisible.value = false
    } else {
      ElMessage.error(response.data.message || '解析失败')
    }
  } catch (error) {
    console.error('解析失败:', error)
    ElMessage.error('解析失败，请检查链接是否正确')
  } finally {
    parseLoading.value = false
  }
}

const mapDialogVisible = ref(false)
const mapKey = ref('')
const tempLocation = reactive({ lng: null, lat: null })

const goToDashboard = (id, name) => {
  router.push(`/store-dashboard?id=${id}&name=${encodeURIComponent(name)}&type=store`)
}

// 经度变化处理
const handleLngChange = (val) => {
  if (val === null || val === undefined || val === '') {
    formData.lng = null
    return
  }
  const num = parseFloat(val)
  if (!isNaN(num) && num >= -180 && num <= 180) {
    formData.lng = num
  } else {
    ElMessage.warning('经度范围应在 -180 到 180 之间')
    lngInput.value = formData.lng !== null ? String(formData.lng) : ''
  }
}

// 纬度变化处理
const handleLatChange = (val) => {
  if (val === null || val === undefined || val === '') {
    formData.lat = null
    return
  }
  const num = parseFloat(val)
  if (!isNaN(num) && num >= -90 && num <= 90) {
    formData.lat = num
  } else {
    ElMessage.warning('纬度范围应在 -90 到 90 之间')
    latInput.value = formData.lat !== null ? String(formData.lat) : ''
  }
}

const businessHours = computed({
  get: () => {
    if (formData.business_hours) {
      const times = formData.business_hours.split('-')
      return times.length === 2 ? times : ['09:00:00', '22:00:00']
    }
    return ['09:00:00', '22:00:00']
  },
  set: (val) => {
    if (val && val.length === 2) {
      formData.business_hours = `${val[0]}-${val[1]}`
    } else {
      formData.business_hours = ''
    }
  }
})

const formRules = {
  store_name: [{ required: true, message: '请输入门店名称', trigger: 'blur' }],
  address: [{ required: true, message: '请输入门店地址', trigger: 'blur' }]
}

const pagination = reactive({
  page: 1,
  limit: 6,
  total: 0
})



const generateStoreNo = () => {
  const date = new Date()
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0')
  return `S${year}${month}${day}${random}`
}



// 修改 fetchData 函数，同时获取列表和统计数据
const fetchData = async () => {
  loading.value = true
  try {
    // 并行请求列表和统计数据
    const [listResponse, statsResponse] = await Promise.all([
      axios.get(`${config.BASE_URL}/shop/api/group_store_api.php`, {
        params: {
          agent_id: agentId,
          store_id: storeUserId,
          page: pagination.page,
          limit: pagination.limit,
          search: searchKey.value,
          status: statusFilter.value
        }
      }),
      axios.get(`${config.BASE_URL}/shop/api/group_store_api.php`, {
        params: {
          action: 'statistics',
          agent_id: agentId,
          store_id: storeUserId
        }
      })
    ])

    if (listResponse.data.code === 200) {
      storeList.value = listResponse.data.data.list.map(item => ({
        ...item,
        features: item.features ? (typeof item.features === 'string' ? JSON.parse(item.features) : item.features) : [],
        images: item.images ? (typeof item.images === 'string' ? JSON.parse(item.images) : item.images) : []
      }))
      pagination.total = listResponse.data.data.pagination.total
    } else {
      ElMessage.error(listResponse.data.message || '获取数据失败')
    }

    if (statsResponse.data.code === 200) {
      statistics.value = statsResponse.data.data
    }
  } catch (error) {
    console.error('获取门店列表失败:', error)
    ElMessage.error('获取数据失败，请稍后重试')
  } finally {
    loading.value = false
  }
}


const handleSearch = () => {
  pagination.page = 1
  fetchData()
}

const resetSearch = () => {
  searchKey.value = ''
  statusFilter.value = null
  pagination.page = 1
  fetchData()
}

const openAddDialog = () => {
  isEditMode.value = false
  resetForm()
  formData.store_no = generateStoreNo()
  dialogVisible.value = true
}

// 修改 openEditDialog 方法，同步经纬度输入框
const openEditDialog = (store) => {
  isEditMode.value = true
  Object.assign(formData, {
    ...store,
    features: store.features || [],
    images: store.images || [],
    business_hours: store.business_hours || '09:00:00-22:00:00'
  })
  lngInput.value = store.lng !== null && store.lng !== undefined ? String(store.lng) : ''
  latInput.value = store.lat !== null && store.lat !== undefined ? String(store.lat) : ''
  imageFiles.value = (store.images || []).map((url, index) => ({
    uid: Date.now() + index,
    name: `image-${index}`,
    url: url
  }))
  dialogVisible.value = true
}
// 修改 resetForm 方法，重置经纬度输入框
const resetForm = () => {
  Object.assign(formData, {
    id: null,
    agent_id: agentId,
    store_id: storeUserId,
    store_no: '',
    store_name: '',
    address: '',
    province: '',
    city: '',
    district: '',
    lng: null,
    lat: null,
    contact_phone: '',
    business_hours: '09:00:00-22:00:00',
    avg_price: 0,
    rating: 0,
    rating_count: 0,
    features: [],
    images: [],
    status: 1,
    sort_order: 0
  })
  imageFiles.value = []
  lngInput.value = ''
  latInput.value = ''
}

const handleImageChange = async (file, fileList) => {
  if (!file.raw) return
  const isImage = file.raw.type.startsWith('image/')
  if (!isImage) {
    ElMessage.error('只能上传图片文件')
    return false
  }
  const isLt5M = file.raw.size / 1024 / 1024 < 5
  if (!isLt5M) {
    ElMessage.error('图片大小不能超过 5MB')
    return false
  }
  try {
    const signature = await getUploadSignature('image')
    const ossRes = await uploadToOSS(file.raw, signature)
    formData.images.push(ossRes.url)
    imageFiles.value = fileList
    ElMessage.success('上传成功')
  } catch (error) {
    console.error('上传失败:', error)
    ElMessage.error('上传失败')
  }
}

const handleImageRemove = (file, fileList) => {
  const index = formData.images.findIndex(img => img === file.url)
  if (index !== -1) formData.images.splice(index, 1)
  imageFiles.value = fileList
}

const submitForm = async () => {
  if (!formRef.value) return
  await formRef.value.validate(async (valid) => {
    if (!valid) return
    submitting.value = true
    const loadingInstance = ElLoading.service({ text: '保存中...' })
    try {
      const payload = {
        ...formData,
        features: JSON.stringify(formData.features),
        images: JSON.stringify(formData.images)
      }
      delete payload.id
      delete payload.create_time
      delete payload.update_time
      const method = isEditMode.value ? 'put' : 'post'
      let url = `${config.BASE_URL}/shop/api/group_store_api.php`
      if (isEditMode.value) payload.id = formData.id
      const response = await axios[method](url, payload)
      if (response.data.code === 200) {
        ElMessage.success(isEditMode.value ? '更新成功' : '新增成功')
        dialogVisible.value = false
        fetchData()
      } else {
        ElMessage.error(response.data.message || '操作失败')
      }
    } catch (error) {
      console.error('提交失败:', error)
      ElMessage.error('操作失败，请稍后重试')
    } finally {
      submitting.value = false
      loadingInstance.close()
    }
  })
}

const handleDelete = async (id) => {
  const loadingInstance = ElLoading.service({ text: '删除中...' })
  try {
    const response = await axios.delete(`${config.BASE_URL}/shop/api/group_store_api.php`, {
      data: { id, store_id: storeUserId }
    })
    if (response.data.code === 200) {
      ElMessage.success('删除成功')
      fetchData()
    } else {
      ElMessage.error(response.data.message || '删除失败')
    }
  } catch (error) {
    console.error('删除失败:', error)
    ElMessage.error('删除失败，请稍后重试')
  } finally {
    loadingInstance.close()
  }
}

const viewStoreDetail = (store) => {
  openEditDialog(store)
}

const fetchMapKey = async () => {
  try {
    const res = await axios.get(`${config.BASE_URL}/shop/api/get_map_key.php`, {
      params: { store_id: storeUserId }
    })
    if (res.data.code === 200) {
      mapKey.value = res.data.data.key
    } else {
      ElMessage.error('地图服务初始化失败')
    }
  } catch (error) {
    console.error('获取地图密钥失败', error)
  }
}

const openMapPicker = async () => {
  if (!mapKey.value) await fetchMapKey()
  if (!mapKey.value) {
    ElMessage.error('无法获取地图密钥，请联系管理员')
    return
  }
  tempLocation.lng = formData.lng
  tempLocation.lat = formData.lat
  mapDialogVisible.value = true
}

// 格式化坐标显示（保留6位小数）
const formatCoordinate = (coord) => {
  if (coord === null || coord === undefined) return ''
  return typeof coord === 'number' ? coord.toFixed(6) : coord
}

// 修改 handleMapConfirm 方法，更新后同步输入框
const handleMapConfirm = (locationData) => {
  console.log('接收到的地图数据:', locationData)
  if (locationData) {
    formData.lng = locationData.lng
    formData.lat = locationData.lat
    formData.address = locationData.address || ''
    formData.province = locationData.province || ''
    formData.city = locationData.city || ''
    formData.district = locationData.district || ''
    // 同步输入框显示
    lngInput.value = String(locationData.lng)
    latInput.value = String(locationData.lat)
  }
  mapDialogVisible.value = false
}

onMounted(() => {
  fetchData()
  fetchMapKey()
})
</script>

<style scoped lang="scss">
.store-management {
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    .page-title {
      font-size: 24px;
      font-weight: 600;
      color: #1a1a2e;
      margin-bottom: 4px;
    }

    .page-desc {
      font-size: 14px;
      color: #8c8c8c;
    }
  }

  .filter-bar {
    margin-bottom: 24px;

    .filter-left {
      display: flex;
      gap: 12px;
      flex-wrap: wrap;
    }
  }

  .location-tip {
    margin-top: 8px;
    padding: 6px 12px;
    background: #f0f9eb;
    border-radius: 6px;
    font-size: 12px;
    color: #67c23a;
    display: flex;
    align-items: center;
    gap: 6px;

    .el-icon {
      font-size: 14px;
    }
  }

  .parse-dialog {
    .parse-content {
      .parse-tips {
        margin-top: 16px;
        padding: 12px;
        background: #f5f7fa;
        border-radius: 8px;

        .tips-title {
          font-size: 14px;
          font-weight: 500;
          margin-bottom: 12px;
          color: #1a1a2e;
        }

        .tips-buttons {
          display: flex;
          gap: 16px;
          margin-bottom: 8px;
          flex-wrap: wrap;

          .tip-link {
            cursor: pointer;
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 14px;
            transition: all 0.2s;

            &.tencent {
              background: #e6f7ff;
              color: #1890ff;

              &:hover {
                background: #bae7ff;
              }
            }

            &.baidu {
              background: #e6f0ff;
              color: #3b6eff;

              &:hover {
                background: #d4e2ff;
              }
            }

            &.amap {
              background: #e6ffe6;
              color: #52c41a;

              &:hover {
                background: #b7eb8f;
              }
            }
          }
        }

        .tips-desc {
          font-size: 12px;
          color: #999;
          margin-top: 8px;
        }
      }
    }
  }

  .tutorial-image-container {
    display: flex;
    justify-content: center;
    align-items: center;
    max-height: 70vh;
    overflow-y: auto;

    img {
      max-width: 100%;
      border-radius: 8px;
    }
  }

  .stat-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 32px;

    .stat-card {
      background: #fff;
      border-radius: 16px;
      padding: 20px;
      display: flex;
      align-items: center;
      gap: 16px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
      transition: all 0.3s;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }

      .stat-icon {
        width: 56px;
        height: 56px;
        border-radius: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .stat-info {
        .stat-value {
          font-size: 28px;
          font-weight: 700;
          color: #1a1a2e;
          line-height: 1.2;
        }

        .stat-label {
          font-size: 13px;
          color: #8c8c8c;
          margin-top: 4px;
        }
      }
    }
  }

  .store-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
    gap: 24px;
    margin-bottom: 24px;

    .store-card {
      background: #fff;
      border-radius: 16px;
      overflow: hidden;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
      transition: all 0.3s;
      cursor: pointer;

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
      }

      .store-cover {
        position: relative;
        height: 200px;
        overflow: hidden;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s;
        }

        &:hover img {
          transform: scale(1.05);
        }

        .store-status {
          position: absolute;
          top: 12px;
          right: 12px;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;

          &.status-active {
            background: rgba(103, 194, 58, 0.9);
            color: #fff;
          }

          &.status-closed {
            background: rgba(0, 0, 0, 0.6);
            color: #fff;
          }
        }
      }

      .store-info {
        padding: 16px;

        .store-name {
          font-size: 16px;
          font-weight: 600;
          color: #1a1a2e;
          margin-bottom: 8px;
        }

        .store-address {
          display: flex;
          align-items: flex-start;
          gap: 6px;
          font-size: 13px;
          color: #666;
          margin-bottom: 12px;

          .el-icon {
            flex-shrink: 0;
            margin-top: 2px;
          }

          span {
            flex: 1;
            line-height: 1.4;
          }
        }

        .store-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;

          .store-rating {
            display: flex;
            align-items: center;
            gap: 6px;

            :deep(.el-rate) {
              height: auto;
            }

            .rating-score {
              font-size: 14px;
              font-weight: 600;
              color: #ff9a2e;
            }
          }

          .store-price {
            font-size: 14px;
            color: #f56c6c;
            font-weight: 500;
          }
        }

        .store-features {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
      }

      .store-actions {
        padding: 12px 16px;
        border-top: 1px solid #f0f0f0;
        display: flex;
        gap: 16px;
      }
    }
  }

  .empty-state {
    grid-column: 1 / -1;
    padding: 60px 0;
  }

  .pagination-wrapper {
    display: flex;
    justify-content: flex-end;
    margin-top: 24px;
  }
}

.store-dialog {
  :deep(.el-dialog__body) {
    max-height: 60vh;
    overflow-y: auto;
  }

  .image-uploader {
    :deep(.el-upload--picture-card) {
      width: 100px;
      height: 100px;
    }

    :deep(.el-upload-list--picture-card .el-upload-list__item) {
      width: 100px;
      height: 100px;
    }
  }

  .upload-tip {
    font-size: 12px;
    color: #999;
    margin-top: 8px;
  }
}

.dashboard-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  border: none !important;
  color: #fff !important;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  .el-icon {
    margin-right: 4px;
  }
}

.location-info {
  margin-top: 8px;
  color: #67c23a;
  font-size: 13px;
}

@media (max-width: 768px) {
  .store-management {
    .stat-cards {
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;

      .stat-card {
        padding: 12px;

        .stat-icon {
          width: 40px;
          height: 40px;

          .el-icon {
            font-size: 20px !important;
          }
        }

        .stat-info .stat-value {
          font-size: 20px;
        }
      }
    }

    .store-grid {
      grid-template-columns: 1fr;
    }
  }
}

@media (max-width: 480px) {
  .store-management {
    .page-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
    }

    .filter-bar .filter-left {
      flex-direction: column;

      .el-input,
      .el-select {
        width: 100% !important;
      }
    }

    .stat-cards {
      grid-template-columns: 1fr;
    }
  }
}
</style>